import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import utc from 'dayjs/plugin/utc';
import { History as HistoryIcon, Search, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { httpClient } from '../../common/httpclient';
import FilterToolbar, { FilterChips } from '../../component/filter/FilterToolbar';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import StatusChip from '../../component/common/StatusChip';
import DefaultTable from '../../component/table/DefaultTable';
import AuditDetailDrawer from './AuditDetailDrawer';
import { classifyAuditAction, formatDateTimeUTC } from './auditUtils';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import MobileCrudList from '../../component/common/MobileCrud';
import { KitChip, KitChips, KitField, KitInput, KitSelect } from '../../component/common/MobileKitExtras';
import { protoIcon } from '../../component/common/ProtoIcon';

const AUDIT_ICONS = {
    criou: { icon: protoIcon('plus'), tone: 'green' },
    editou: { icon: protoIcon('edit'), tone: 'amber' },
    excluiu: { icon: protoIcon('ban'), tone: 'red' },
    login: { icon: protoIcon('user'), tone: undefined },
    outro: { icon: protoIcon('history'), tone: 'gray' },
};
const HistoryProtoIcon = protoIcon('history');

dayjs.extend(utc);

// Log de auditoria (`/audit/logs`) — DESIGN-SYSTEM.md §6.1 (template de
// lista), `prototipo-log-auditoria.html`. Mesma estrutura de
// `report-toolbar` + `chips-row` + `DefaultTable` já usada em
// `pages/categories/List.js`/`pages/transactions/List.js`, mas sem coluna de
// ações — a linha inteira é clicável e abre `AuditDetailDrawer` (aba
// Detalhes/Alterações), nunca um modal central (decisão consciente do
// FRONTEND-TASKS.md, item "Drawer de detalhe de auditoria").
//
// `GET /audit/logs` (sem `resource`/`resource_id` de query) devolve todos os
// registros da empresa do usuário logado de uma vez (`AuditLogResponse`:
// `id`, `user_name`, `action`, `resource`, `created_at` — ver
// `fv-api/auditlog/audit.go`/`repository.go`). Por isso todo filtro desta
// tela (Ação/Módulo/Período/Busca) é 100% client-side sobre esse conjunto já
// carregado, igual ao protótipo (`applyFilters()`/`LOGS.filter(...)` em
// memória) — não existe filtro server-side de ação/data hoje na API.
//
// GAP DE BACKEND DOCUMENTADO (sem inventar dado): `prototipo-log-auditoria.html`
// desenha "Endereço IP"/"Dispositivo" na aba Detalhes do drawer e um
// sub-rótulo "Recurso · #ID" na tabela. A tabela `audit_log` até tem colunas
// `ip_address`/`user_agent`/`resource_id`, mas (1) `GetLogs`/`GetDetails`
// (`fv-api/auditlog/repository.go`) nunca fazem SELECT dessas colunas — não
// chegam no JSON de resposta — e (2) nenhum dos ~20 pontos de chamada do
// serviço de auditoria hoje preenche `AuditEntry.IPAddress`/`UserAgent`
// (`grep IPAddress\|UserAgent fv-api --include=*.go`), então mesmo expondo a
// coluna ela viria vazia para praticamente todo registro. A única exceção
// real é o login (`user/auth.go`), que grava IP/User-Agent dentro do próprio
// `AfterData` (`IP_Address`/`User_Agent`) — esses aparecem normalmente na aba
// Alterações de um registro de login, só não na aba Detalhes. Corrigir isso
// exigiria alterar o contrato de `/audit/logs`/`/audit/diff/{id}` em
// `fv-api` (novo SELECT + campos no `AuditLogResponse`) — mudança de
// backend fora do escopo desta tarefa de redesign visual; sinalizado aqui
// para o usuário decidir se vale abrir como tarefa própria. Por ora a aba
// Detalhes mostra "Não disponível" para os dois campos, e a tabela usa só
// `resource` (sem sub-ID).
export default function AuditLogsList() {
    const isMobileShell = useIsMobileShell();
    const [loading, setLoading] = useState(true);
    const [allData, setAllData] = useState([]);
    // Mobile: filtro por usuário (folha de filtros) — client-side como os demais.
    const [mobileLimit, setMobileLimit] = useState(30);
    const [draftUser, setDraftUser] = useState('');
    const [userFilter, setUserFilter] = useState('');

    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState('success');
    const [openSnackbar, setOpenSnackbar] = useState(false);

    // Filtros aplicados (usados para filtrar `allData`) vs. rascunho (o que
    // está digitado/selecionado na toolbar até o usuário clicar "Buscar") —
    // mesmo padrão de `pages/transactions/List.js`, espelhando
    // `applyFilters()`/`clearFilters()` do protótipo.
    const [draftAction, setDraftAction] = useState('');
    const [draftModule, setDraftModule] = useState('');
    const [draftDateFrom, setDraftDateFrom] = useState(null);
    const [draftDateTo, setDraftDateTo] = useState(null);
    const [draftSearch, setDraftSearch] = useState('');

    const [action, setAction] = useState('');
    const [moduleFilter, setModuleFilter] = useState('');
    const [dateFrom, setDateFrom] = useState(null);
    const [dateTo, setDateTo] = useState(null);
    const [search, setSearch] = useState('');

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedAuditId, setSelectedAuditId] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);

    const fetchData = async () => {
        try {
            const response = await httpClient.get('/audit/logs');
            if (response.status === 200 && response.data) {
                setAllData(Array.isArray(response.data) ? response.data : []);
            } else {
                setSeverity('error');
                setMessage('Erro ao carregar logs de auditoria');
                setOpenSnackbar(true);
                setAllData([]);
            }
        } catch (error) {
            console.error('Error fetching audit logs:', error);
            setSeverity('error');
            setMessage('Erro ao carregar logs de auditoria');
            setOpenSnackbar(true);
            setAllData([]);
        }
    };

    useEffect(() => {
        fetchData().finally(() => setLoading(false));
    }, []);

    // Módulo é um select dinâmico com os valores reais de `resource`
    // presentes nos dados carregados — o protótipo tem uma lista fixa
    // (Produto/Cliente/Fornecedor/...), mas a API grava `resource` de forma
    // inconsistente (ora uma categoria genérica como "Produto"/"Venda", ora
    // o nome específico do registro excluído, ex. `cardmachine/deleter.go`
    // usa `Resource: cardMachine.Name`) — uma lista fixa deixaria de
    // combinar com boa parte dos registros reais. Refletir só o que
    // realmente existe evita um filtro que parece funcionar mas nunca bate
    // com nada.
    const moduleOptions = useMemo(() => {
        const set = new Set();
        allData.forEach((row) => { if (row.resource) set.add(row.resource); });
        return Array.from(set).sort((a, b) => a.localeCompare(b, 'pt-BR'));
    }, [allData]);

    const filteredRows = useMemo(() => {
        return allData.filter((row) => {
            if (action && classifyAuditAction(row.action) !== action) return false;
            if (moduleFilter && row.resource !== moduleFilter) return false;
            if (userFilter && row.user_name !== userFilter) return false;
            if (dateFrom || dateTo) {
                const rowDate = dayjs.utc(row.created_at).format('YYYY-MM-DD');
                if (dateFrom && rowDate < dateFrom.format('YYYY-MM-DD')) return false;
                if (dateTo && rowDate > dateTo.format('YYYY-MM-DD')) return false;
            }
            if (search) {
                const term = search.toLowerCase();
                const haystack = `${row.user_name || ''} ${row.action || ''} ${row.resource || ''}`.toLowerCase();
                if (!haystack.includes(term)) return false;
            }
            return true;
        });
    }, [allData, action, moduleFilter, userFilter, dateFrom, dateTo, search]);

    const userOptions = useMemo(() => {
        const set = new Set();
        allData.forEach((row) => { if (row.user_name) set.add(row.user_name); });
        return Array.from(set).sort((a, b) => a.localeCompare(b, 'pt-BR'));
    }, [allData]);

    const applyFilters = () => {
        setUserFilter(draftUser);
        setAction(draftAction);
        setModuleFilter(draftModule);
        setDateFrom(draftDateFrom);
        setDateTo(draftDateTo);
        setSearch(draftSearch.trim());
    };

    const clearFilters = () => {
        setDraftUser('');
        setUserFilter('');
        setDraftAction('');
        setDraftModule('');
        setDraftDateFrom(null);
        setDraftDateTo(null);
        setDraftSearch('');
        setAction('');
        setModuleFilter('');
        setDateFrom(null);
        setDateTo(null);
        setSearch('');
    };

    const removeActionFilter = () => { setDraftAction(''); setAction(''); };
    const removeModuleFilter = () => { setDraftModule(''); setModuleFilter(''); };
    const removePeriodFilter = () => { setDraftDateFrom(null); setDraftDateTo(null); setDateFrom(null); setDateTo(null); };
    const removeSearchFilter = () => { setDraftSearch(''); setSearch(''); };

    const handleRowClick = (row) => {
        setSelectedRow(row);
        setSelectedAuditId(row.id);
        setDrawerOpen(true);
    };

    const closeDrawer = () => {
        setDrawerOpen(false);
        setSelectedAuditId(null);
        setSelectedRow(null);
    };

    const columns = [
        {
            name: 'Usuário',
            path: 'user_name',
            style: { width: '24%', minWidth: '170px' },
            render: (row) => (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                   <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.user_name}>
                        {row.user_name || '—'}
                    </Typography>
                </Box>
            ),
        },
        {
            name: 'Ação',
            path: 'action',
            style: { width: '32%', minWidth: '220px' },
            render: (row) => (
                <StatusChip
                    domain="auditAction"
                    value={classifyAuditAction(row.action)}
                    label={row.action || '—'}
                    sx={{ maxWidth: '100%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                />
            ),
        },
        {
            name: 'Recurso',
            path: 'resource',
            style: { width: '20%', minWidth: '140px' },
            render: (row) => (
                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.resource}>
                    {row.resource || '—'}
                </Typography>
            ),
        },
        {
            name: 'Data/Hora',
            path: 'created_at',
            style: { width: '24%', minWidth: '150px' },
            render: (row) => {
                const { datePart, timePart } = formatDateTimeUTC(row.created_at);
                return (
                    <Box sx={{ textAlign: 'left' }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>{datePart}</Typography>
                        <Typography sx={{ fontSize: '11px', color: 'text.disabled', mt: '1px' }}>{timePart}</Typography>
                    </Box>
                );
            },
        },
    ];

    const actionFilterLabel = action ? (STATUS_TABLE_LABELS[action] || action) : 'Todas';

    // Shell mobile: lista do protótipo `sistema/auditoria.html` (busca, chips, folha de filtros, detalhe em folha).
    if (isMobileShell) {
        const toDayjs = (value) => (value ? dayjs(value, 'YYYY-MM-DD') : null);
        const fromDayjs = (value) => (value ? value.format('YYYY-MM-DD') : '');
        const chipOptions = [
            { value: '', label: 'Tudo' },
            { value: 'criou', label: 'Criou' },
            { value: 'editou', label: 'Editou' },
            { value: 'excluiu', label: 'Excluiu' },
            { value: 'login', label: 'Acessos' },
        ];
        const sortedRows = [...filteredRows].sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
        return (
            <PageScaffold links={[]} allowBleed titlePageTarget="Auditoria" message={message} openSnackbar={openSnackbar} setOpenSnackbar={setOpenSnackbar} severity={severity}>
                <MobileCrudList
                    items={sortedRows.slice(0, mobileLimit)}
                    hasMore={sortedRows.length > mobileLimit}
                    onLoadMore={() => setMobileLimit((current) => current + 30)}
                    getKey={(row) => row.id}
                    getRow={(row) => {
                        const meta = AUDIT_ICONS[classifyAuditAction(row.action)];
                        const { datePart, timePart } = formatDateTimeUTC(row.created_at);
                        return {
                            icon: meta.icon,
                            tone: meta.tone,
                            title: row.action || '—',
                            sub: `${row.user_name || '—'} · ${datePart}${timePart ? ` ${timePart}` : ''}`,
                            chevron: true,
                        };
                    }}
                    loading={loading}
                    search={draftSearch}
                    onSearchChange={(value) => { setDraftSearch(value); setSearch(value.trim()); }}
                    searchPlaceholder="Buscar por usuário ou ação…"
                    topSlot={(
                        <KitChips sx={{ mt: '10px' }}>
                            {chipOptions.map((opt) => (
                                <KitChip
                                    key={opt.label}
                                    active={action === opt.value}
                                    onClick={() => { setAction(opt.value); setDraftAction(opt.value); }}
                                >
                                    {opt.label}
                                </KitChip>
                            ))}
                        </KitChips>
                    )}
                    filter={{
                        active: Boolean(moduleFilter || userFilter || dateFrom || dateTo),
                        onOpen: () => {
                            setDraftUser(userFilter);
                            setDraftAction(action);
                            setDraftModule(moduleFilter);
                            setDraftDateFrom(dateFrom);
                            setDraftDateTo(dateTo);
                        },
                        onApply: applyFilters,
                        onClear: clearFilters,
                        children: (
                            <>
                                <KitField label="Usuário">
                                    <KitSelect value={draftUser} onChange={(e) => setDraftUser(e.target.value)} aria-label="Usuário">
                                        <option value="">Todos</option>
                                        {userOptions.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                                    </KitSelect>
                                </KitField>
                                <KitField label="Ação">
                                    <KitSelect value={draftAction} onChange={(e) => setDraftAction(e.target.value)} aria-label="Ação">
                                        <option value="">Todas</option>
                                        <option value="criou">Criou</option>
                                        <option value="editou">Editou</option>
                                        <option value="excluiu">Excluiu</option>
                                        <option value="login">Login</option>
                                        <option value="outro">Outro</option>
                                    </KitSelect>
                                </KitField>
                                <KitField label="Módulo">
                                    <KitSelect value={draftModule} onChange={(e) => setDraftModule(e.target.value)} aria-label="Módulo">
                                        <option value="">Todos</option>
                                        {moduleOptions.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                                    </KitSelect>
                                </KitField>
                                <KitField label="De">
                                    <KitInput type="date" value={fromDayjs(draftDateFrom)} onChange={(e) => setDraftDateFrom(toDayjs(e.target.value))} aria-label="De" />
                                </KitField>
                                <KitField label="Até">
                                    <KitInput type="date" value={fromDayjs(draftDateTo)} onChange={(e) => setDraftDateTo(toDayjs(e.target.value))} aria-label="Até" />
                                </KitField>
                            </>
                        ),
                    }}
                    emptyIcon={HistoryProtoIcon}
                    emptyTitle="Nenhum registro encontrado"
                    emptyText="Ajuste a busca ou os filtros para ver outros registros."
                    onOpen={handleRowClick}
                />
                <AuditDetailDrawer
                    open={drawerOpen}
                    onClose={closeDrawer}
                    auditId={selectedAuditId}
                    row={selectedRow}
                />
            </PageScaffold>
        );
    }

    return (
        <PageScaffold links={[]} titlePageTarget={'Log de auditoria'} message={message} openSnackbar={openSnackbar} setOpenSnackbar={setOpenSnackbar} severity={severity}>
            <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                {/* page-head */}
                <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'flex-end' }, justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 38, height: 38, borderRadius: '11px', flexShrink: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#fff', background: (t) => t.palette.gradient.brand,
                        }}>
                            <HistoryIcon size={18} strokeWidth={2} />
                        </Box>
                        <Box>
                            <Typography component="h1" data-page-title sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                                Log de auditoria
                            </Typography>
                            <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                                Histórico de ações realizadas no sistema — quem fez, o que mudou e quando. Clique em um registro para ver o detalhe.
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* report-toolbar */}
                <FilterToolbar
                    activeCount={(draftAction ? 1 : 0) + (draftModule ? 1 : 0) + (draftDateFrom || draftDateTo ? 1 : 0)}
                    onClear={clearFilters}
                    onApply={applyFilters}
                    primary={(
                        <Box sx={{ flex: '0 0 240px' }}>
                            <TbLabel>Buscar</TbLabel>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="Usuário, ação ou recurso…"
                                value={draftSearch}
                                onChange={(e) => setDraftSearch(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><Search size={15} strokeWidth={2} /></InputAdornment>,
                                    sx: { height: 40, fontSize: '13px', borderRadius: TB_FIELD_RADIUS },
                                }}
                            />
                        </Box>
                    )}
                    actions={(
                        <Stack direction="row" spacing={1}>
                            <Button variant="outlined" size="small" onClick={clearFilters} sx={{ height: '33px' }}>Limpar</Button>
                            <Button variant="contained" size="small" startIcon={<Search size={14} strokeWidth={2} />} onClick={applyFilters} sx={{ height: '33px' }}>
                                Buscar
                            </Button>
                        </Stack>
                    )}
                >
                        <Box>
                            <TbLabel>Ação</TbLabel>
                            <FormControl size="small" sx={{ minWidth: 150 }}>
                                <Select
                                    value={draftAction}
                                    displayEmpty
                                    onChange={(e) => setDraftAction(e.target.value)}
                                    renderValue={() => (draftAction ? (STATUS_TABLE_LABELS[draftAction] || draftAction) : 'Todas')}
                                    sx={{ height: 40, fontSize: '13px', borderRadius: TB_FIELD_RADIUS }}
                                >
                                    <MenuItem value="">Todas</MenuItem>
                                    <MenuItem value="criou">Criou</MenuItem>
                                    <MenuItem value="editou">Editou</MenuItem>
                                    <MenuItem value="excluiu">Excluiu</MenuItem>
                                    <MenuItem value="login">Login</MenuItem>
                                    <MenuItem value="outro">Outro</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>

                        <Box>
                            <TbLabel>Módulo</TbLabel>
                            <FormControl size="small" sx={{ minWidth: 170 }}>
                                <Select
                                    value={draftModule}
                                    displayEmpty
                                    onChange={(e) => setDraftModule(e.target.value)}
                                    renderValue={() => draftModule || 'Todos'}
                                    sx={{ height: 40, fontSize: '13px', borderRadius: TB_FIELD_RADIUS }}
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    {moduleOptions.map((opt) => (
                                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        <Box>
                            <TbLabel>De</TbLabel>
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                                <DatePicker
                                    format="DD/MM/YYYY"
                                    value={draftDateFrom}
                                    onChange={(newDate) => setDraftDateFrom(newDate)}
                                    slotProps={{ textField: { size: 'small', sx: dateFieldSx } }}
                                />
                            </LocalizationProvider>
                        </Box>

                        <Box>
                            <TbLabel>Até</TbLabel>
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                                <DatePicker
                                    format="DD/MM/YYYY"
                                    value={draftDateTo}
                                    onChange={(newDate) => setDraftDateTo(newDate)}
                                    slotProps={{ textField: { size: 'small', sx: dateFieldSx } }}
                                />
                            </LocalizationProvider>
                        </Box>

                </FilterToolbar>

                {/* chips-row */}
                <FilterChips>
                    {action && (
                        <Chip
                            label={`Ação: ${actionFilterLabel}`}
                            size="small"
                            onDelete={removeActionFilter}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'info.bg', color: 'primary.main' }}
                        />
                    )}
                    {moduleFilter && (
                        <Chip
                            label={`Módulo: ${moduleFilter}`}
                            size="small"
                            onDelete={removeModuleFilter}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                        />
                    )}
                    {(dateFrom || dateTo) && (
                        <Chip
                            label={`Período: ${dateFrom ? dateFrom.format('DD/MM/YYYY') : '…'} – ${dateTo ? dateTo.format('DD/MM/YYYY') : '…'}`}
                            size="small"
                            onDelete={removePeriodFilter}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                        />
                    )}
                    {search && (
                        <Chip
                            label={`Busca: "${search}"`}
                            size="small"
                            onDelete={removeSearchFilter}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                        />
                    )}
                    <Chip
                        label={`${filteredRows.length} registro${filteredRows.length === 1 ? '' : 's'} encontrado${filteredRows.length === 1 ? '' : 's'}`}
                        size="small"
                        sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                    />
                </FilterChips>

                <DefaultTable
                    colunms={columns}
                    data={filteredRows}
                    loading={loading}
                    onRowClick={handleRowClick}
                    rowPerPage={10}
                    rowsPerPageOptions={[8, 10, 20]}
                    itemLabel="registros"
                    sortableFields={['user_name', 'action', 'resource', 'created_at']}
                    defaultSort={{ field: 'created_at', direction: 'desc' }}
                />
            </Box>

            <AuditDetailDrawer
                open={drawerOpen}
                onClose={closeDrawer}
                auditId={selectedAuditId}
                row={selectedRow}
            />
        </PageScaffold>
    );
}

// Micro-label fixo do `tb-field` (DESIGN-SYSTEM.md §5.5/§9), mesmo padrão de
// `pages/categories/List.js`/`pages/transactions/List.js`.
function TbLabel({ children }) {
    return (
        <Typography sx={{ fontSize: '10.5px', lineHeight: '13px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '6px' }}>
            {children}
        </Typography>
    );
}

const TB_FIELD_RADIUS = '9px';

// `DatePicker` (MUI X) renderiza um input "por seções" com classes próprias
// (`MuiPickersOutlinedInput-*`/`MuiPickersSectionList-*`), não as classes de
// `MuiOutlinedInput`/`MuiInputBase` de um `TextField` comum — sem isso o
// campo cai no tamanho default do MUI em vez do `tb-field` (13px, 40px de
// altura, 9px de raio) usado no Select/Busca ao lado (mesma correção já
// aplicada em `pages/transactions/List.js`/`pages/sales/List.js`).
const dateFieldSx = {
    '& .MuiPickersOutlinedInput-root': {
        width: '150px',
        height: 40,
        padding: '0 12px',
        borderRadius: TB_FIELD_RADIUS,
        backgroundColor: 'background.subtle',
    },
    '& .MuiPickersSectionList-sectionContent, & .MuiPickersInputBase-sectionsContainer': {
        fontSize: '13px',
    },
};

const STATUS_TABLE_LABELS = {
    criou: 'Criou',
    editou: 'Editou',
    excluiu: 'Excluiu',
    login: 'Login',
    outro: 'Outro',
};

function getInitials(name) {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    const first = parts[0][0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase();
}
